import {
  DATA_PROVIDER_DISPLAY_NAMES,
  EHR_NETWORK_PROVIDERS,
} from '@ctw/shared/api/fhir/mappings/data-provider-names';
import type {
  PatientHistoryJobProviderStatus,
  PatientHistoryJobResponseJobData,
  PatientHistoryJobStatus,
} from '@ctw/shared/api/patient-history/patient-history-types';
import { format } from 'date-fns';
import type { PatientModel } from './patient';

export const completedProviderStatus: ReadonlyArray<PatientHistoryJobProviderStatus> = [
  'done',
  'skipped',
];

export const failedProviderStatus: ReadonlyArray<PatientHistoryJobProviderStatus> = [
  'error',
  'done_with_info',
];

export type PatientHistoryJobDerivedStatus =
  | 'queued'
  | 'inProgress'
  | 'complete'
  | 'failed'
  | 'partiallyComplete' // Partially complete is a combination of in-progress and complete
  | 'completeWithErrors' // Complete with errors is a combination of complete and failed
  | 'skipped'; // The provider explicitly skipped this job

export type PatientHistoryJobDerivedProvider = {
  isEhrNetwork: boolean;
  service: string;
  serviceDisplayName: string;
  status: PatientHistoryJobProviderStatus;
  derivedStatus: PatientHistoryJobDerivedStatus;
};

export class PatientHistoryRequestModel {
  public kind = 'PatientHistory' as const;

  public patient: PatientModel;
  public historyInfo: PatientHistoryJobResponseJobData;

  public constructor(patient: PatientModel, historyInfo: PatientHistoryJobResponseJobData) {
    this.patient = patient;
    this.historyInfo = historyInfo;
  }

  public get key() {
    return this.historyInfo.id;
  }

  public get rawStatus() {
    return this.historyInfo.attributes.jobStatus;
  }

  public get isScheduled() {
    return (['queued', 'scheduled'] satisfies Array<PatientHistoryJobStatus>).includes(
      this.historyInfo.attributes.jobStatus as never,
    );
  }

  public get isInProgress() {
    return (
      this.historyInfo.attributes.jobStatus === ('in_progress' satisfies PatientHistoryJobStatus)
    );
  }

  public get isCompleted() {
    return (['done', 'error'] satisfies Array<PatientHistoryJobStatus>).includes(
      this.historyInfo.attributes.jobStatus as never,
    );
  }

  public get providers() {
    return this.historyInfo.attributes.providers;
  }

  public get rawTargetDate() {
    // This is necessary because targetDate was not introduced until May 2023 and it was not backfilled
    return new Date(
      this.historyInfo.attributes.targetDate ??
        Number(this.historyInfo.attributes.lastUpdatedAt) * 1000,
    );
  }

  public get targetDate() {
    return format(this.rawTargetDate, 'M/d/yyyy');
  }

  public get createdAt() {
    return format(
      new Date(Number(this.historyInfo.attributes.createdAt) * 1000),
      "M/d/yy 'at' h:mm a",
    );
  }

  public get rawLastUpdatedAt() {
    return new Date(Number(this.historyInfo.attributes.lastUpdatedAt) * 1000);
  }

  public get lastUpdatedAt() {
    return format(this.rawLastUpdatedAt, "M/d/yy 'at' h:mm a");
  }

  public get derivedStatus(): PatientHistoryJobDerivedStatus {
    return getDerivedJobStatus(this.providers.map((provider) => provider.status));
  }

  public get derivedProviders(): Array<PatientHistoryJobDerivedProvider> {
    return this.providers.map((provider) => {
      return {
        isEhrNetwork: EHR_NETWORK_PROVIDERS.includes(provider.service),
        service: provider.service,
        serviceDisplayName: DATA_PROVIDER_DISPLAY_NAMES[provider.service] ?? provider.service,
        status: provider.status,
        derivedStatus: getDerivedProviderStatus(provider.status),
      };
    });
  }

  public get ehrNetworkStatus(): PatientHistoryJobDerivedStatus | undefined {
    const ehrNetworkProviders = this.derivedProviders.filter((provider) => provider.isEhrNetwork);
    if (ehrNetworkProviders.length === 0) {
      return undefined;
    }

    return getDerivedJobStatus(ehrNetworkProviders.map((provider) => provider.status));
  }
}

/**
 * Derive the displayed status for a single provider. `'skipped'` is its own provider-level
 * state ("the provider opted out of this job"). Other statuses delegate to the job-level
 * rollup so the single-provider case reuses the same complete/failed/in-progress semantics.
 */
export const getDerivedProviderStatus = (
  status: PatientHistoryJobProviderStatus,
): PatientHistoryJobDerivedStatus => {
  if (status === 'skipped') {
    return 'skipped';
  }
  return getDerivedJobStatus([status]);
};

/**
 * Roll up an array of provider statuses into an overall job (or sub-group) status.
 * `'skipped'` is counted as completed at this level — a job where every provider was
 * skipped is reported as `'complete'`, not `'skipped'`.
 */
export const getDerivedJobStatus = (
  statuses: Array<PatientHistoryJobProviderStatus>,
): PatientHistoryJobDerivedStatus => {
  if (statuses.every((status) => completedProviderStatus.includes(status))) {
    return 'complete';
  }

  if (statuses.every((status) => failedProviderStatus.includes(status))) {
    return 'failed';
  }

  if (
    statuses.every((status) =>
      [...completedProviderStatus, ...failedProviderStatus].includes(status),
    )
  ) {
    return 'completeWithErrors';
  }

  if (statuses.some((status) => completedProviderStatus.includes(status))) {
    return 'partiallyComplete';
  }

  if (statuses.some((status) => status === 'unset')) {
    return 'queued';
  }

  return 'inProgress';
};
